import type { Shop } from "@mall-mart/shared";
import { Cart, CartItem } from "./Cart.js";
import type { Session } from "../auth/session.js";

/** Persisted shape: shopId -> items. Stored server-side against the
 *  signed-in shopper (Shopify customer metafield). */
export type CartSnapshot = Record<string, CartItem[]>;

/**
 * One Cart per store (cross-store carts are out of scope: each merchant
 * checkout is its own Shopify session). Tracks the "active" cart — the one
 * most recently added to or opened — for HUD/panel display, and aggregates
 * counts across all carts.
 */
export class CartManager {
  private readonly carts: Map<string, Cart> = new Map();
  private readonly serverHttpUrl: string;
  private readonly feedOrigins: Map<string, string>;
  private active: Cart | undefined;
  private listeners: Array<() => void> = [];
  private session: Session | null = null;

  constructor(serverHttpUrl: string, feedOrigins: Map<string, string>) {
    this.serverHttpUrl = serverHttpUrl;
    this.feedOrigins = feedOrigins;
  }

  /** Signed-in session (or null for guests); carts read it live so a login
   *  mid-visit upgrades checkouts without rebuilding carts. */
  setSession(session: Session | null): void {
    this.session = session;
  }

  currentSession(): Session | null {
    return this.session;
  }

  cartFor(shop: Shop): Cart {
    const existing: Cart | undefined = this.carts.get(shop.id);
    if (existing !== undefined) {
      return existing;
    }
    const isFeed: boolean = shop.source === "acp-feed";
    const cart: Cart = new Cart({
      shopId: shop.id,
      shopName: shop.name,
      mode: isFeed ? "permalink" : "cart_api",
      storeOrigin: isFeed ? this.feedOrigins.get(shop.id) : undefined,
      serverHttpUrl: this.serverHttpUrl,
      sessionProvider: () => this.session,
    });
    cart.onChange(() => {
      this.active = cart;
      this.emit();
    });
    this.carts.set(shop.id, cart);
    return cart;
  }

  setActive(shopId: string): void {
    const cart: Cart | undefined = this.carts.get(shopId);
    if (cart !== undefined) {
      this.active = cart;
      this.emit();
    }
  }

  activeCart(): Cart | undefined {
    return this.active;
  }

  /** Carts that currently hold items. */
  nonEmptyCarts(): Cart[] {
    return Array.from(this.carts.values()).filter((c) => c.count() > 0);
  }

  totalCount(): number {
    let n: number = 0;
    for (const c of this.carts.values()) {
      n += c.count();
    }
    return n;
  }

  /** Serializable view of every non-empty cart, for server-side persistence. */
  snapshot(): CartSnapshot {
    const snap: CartSnapshot = {};
    for (const [shopId, cart] of this.carts) {
      const items: CartItem[] = cart.itemList();
      if (items.length > 0) {
        snap[shopId] = items;
      }
    }
    return snap;
  }

  /** Restore a persisted snapshot. Shops that no longer exist are dropped;
   *  local items win over the snapshot only when the local cart is non-empty
   *  (a fresh visit has empty carts, so the saved cart simply loads). */
  restore(snapshot: CartSnapshot, shopsById: Map<string, Shop>): void {
    for (const [shopId, items] of Object.entries(snapshot)) {
      const shop: Shop | undefined = shopsById.get(shopId);
      if (shop === undefined || !Array.isArray(items)) {
        continue;
      }
      const cart: Cart = this.cartFor(shop);
      if (cart.count() === 0) {
        cart.restoreItems(items);
      }
    }
  }

  onChange(fn: () => void): void {
    this.listeners.push(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) {
      fn();
    }
  }
}
